import { useTranslation } from '@/i18n/client';
import { DialogContent, DialogTitle } from '@mui/material';
import to from 'await-to-js';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import CouponCard from '@/app/csr-src/pages/profile/voucher/components/couponCard/index';
import UKDialog from '@/components/UKDialog';
import UKLoadingIcon, { CIRCLE_COLOR_MAP } from '@/components/UKLoadingIcon';
import UKToast from '@/components/UKToast/index';

import { orderCancelRefund } from '@/apis/order/order.api';
import { IOrderDetailReq, RefundDetailInfoDTO } from '@/apis/order/order.interface';
import TipIcon from '@/assets/icons/TipIcon.svg';
import VectorDown from '@/assets/icons/VectorDownBig.svg';
import UKPageNothing, { DefaultImageType } from '@/components/UKPageNothing';
import cn from 'clsx';
import RefundTipsModal from '../refundTipsModal';
import styles from './index.module.scss';

interface refundModalProps {
  orderId: string;
  isOpen: boolean;
  closeHandler: () => void;
  handleConfirmClick: () => void;
}

const refundModal: React.FC<refundModalProps> = observer(
  ({ orderId, isOpen, closeHandler, handleConfirmClick }) => {
    const { t } = useTranslation();

    const [refundDetailData, setRefundDetailData] = useState<RefundDetailInfoDTO | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const [showRefundTipsModal, setShowRefundTipsModal] = useState(false);
    const [isItemVisible, setIsItemVisible] = useState(false);

    const loadRefundDetailData = async () => {
      const requestData: IOrderDetailReq = { orderId: String(orderId) };
      const [err, cancelRefundData] = await to(orderCancelRefund(requestData));

      if (err) {
        setError('Failed to fetch order details');
        setRefundDetailData(null);
      } else {
        setRefundDetailData(cancelRefundData as RefundDetailInfoDTO);
        setError(null);
      }

      setLoading(false);
    };

    useEffect(() => {
      loadRefundDetailData();
    }, []);

    // 复制售后单号
    const copyHandler = () => {
      UKToast.show({
        content: t('order.orderDetail.common.copySuccessTips'), // 弹窗内容
        icon: 'success', // 可选：success, fail, loading, warn, info，影响图标
        duration: 2000, // 不写的话默认3000
      });
    };

    // 展开与收起功能
    const toggleExpand = () => {
      setIsItemVisible(!isItemVisible);
    };

    return (
      <>
        <UKDialog
          open={isOpen}
          onClose={closeHandler}
          showCloseBtn={true}
          className={styles.refund_modal}
        >
          <DialogTitle
            sx={{
              fontSize: '18px',
            }}
          >
            {t('order.orderDetail.refundModal.modalTitle')}
          </DialogTitle>
          <DialogContent>
            {loading ? (
              <div className={styles.loading_wrap}>
                <UKLoadingIcon type={CIRCLE_COLOR_MAP.BLACK} />
                <span className={styles.loading_text}>{t('common.pageStatus.loading')}</span>
              </div>
            ) : refundDetailData ? (
              <div className={styles.refund_wrap}>
                <div className={styles.content_wrap}>
                  <div className={styles.refund_item_tip}>
                    <div>{refundDetailData?.refundAmountTitle}</div>
                    <TipIcon
                      className={styles.tip_ico}
                      onClick={() => {
                        setShowRefundTipsModal(true);
                      }}
                    />
                  </div>
                  <div className={styles.refund_item_title}>
                    <div className={styles.refund_item_title_left}>
                      {t('afterSales.refundDetails.refundAmount')}
                    </div>
                    <div className={styles.price_wrap}>{refundDetailData?.refundAmount}</div>
                  </div>
                  {!!refundDetailData?.refundItems?.length && (
                    <ul className={styles.priceDetailWrap}>
                      {refundDetailData?.refundItems?.map((item, index) => {
                        return (
                          <li key={`refundChildPrice${index}`}>
                            <div>{item?.title}</div>
                            <div>
                              {refundDetailData?.localInfo?.currencySymbol}
                              {item?.amount}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {refundDetailData?.showReturnPoints && (
                    <div className={styles.refund_item_title}>
                      <div className={styles.refund_item_title_left}>
                        {t('afterSales.refundDetails.returnedPoints')}
                      </div>
                      <div className={styles.price_wrap}>{refundDetailData?.returnPoints}</div>
                    </div>
                  )}
                  {!!refundDetailData?.couponInfos?.length && (
                    <div className={styles.refund_item_title}>
                      <div className={styles.refund_item_title_left}>
                        {t('afterSales.refundDetails.returnedCoupon')}
                      </div>
                      <div onClick={toggleExpand} className={styles.expand_wrap}>
                        <VectorDown
                          className={cn(styles.more_ico, {
                            [styles.retract_ico]: isItemVisible,
                          })}
                        />
                      </div>
                    </div>
                  )}
                  {isItemVisible && !!refundDetailData?.couponInfos?.length && (
                    <div>
                      <CouponCard
                        data={refundDetailData?.couponInfos}
                        currentTab='available'
                        containerStyle={{ width: '100%', marginBottom: '8px' }}
                        contentStyle={{ width: '375px' }}
                        isShowUseBtn={false}
                        currency={refundDetailData?.localInfo?.currencySymbol}
                      />
                    </div>
                  )}
                </div>
                {!!refundDetailData?.items?.length && (
                  <div className={styles.base_info_wrap}>
                    {refundDetailData?.items.map((item, index) => {
                      return (
                        <div className={styles.base_info_item} key={index}>
                          <div>{item?.label}</div>
                          <div>
                            {item?.value}
                            {item?.showCopy && (
                              <CopyToClipboard text={item?.value} onCopy={() => copyHandler()}>
                                <span className={styles.copy_btn}>
                                  {t('order.orderDetail.common.copyText')}
                                </span>
                              </CopyToClipboard>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <UKPageNothing
                defaultImageType={DefaultImageType.DEFAULT_NOTING}
                style={{ padding: '60px 0' }}
                text={t('order.orderList.emptyPage.refundTip')}
              />
            )}
          </DialogContent>
        </UKDialog>
        {showRefundTipsModal && (
          <RefundTipsModal
            isOpen={showRefundTipsModal}
            content={refundDetailData?.refundAmountTips}
            closeHandler={() => setShowRefundTipsModal(false)}
          />
        )}
      </>
    );
  },
);

export default refundModal;
